import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import { PermissionSetType } from 'api/models';
import { TimesheetApprovalType } from 'api/timesheets/configuration/configuration';
import {
  getCountOfTimesheetsByFilter,
  getCountOfTimesheetsByRole,
  getTimesheetsByFilter,
  getTimesheetsByRole,
} from 'components/organisms/timesheets/common/utils';
import {
  FilterItem,
  FilterViewOptions,
  FilterViewType,
  UserTimesheet,
} from 'components/organisms/timesheets/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { useUserPermissionSetType } from 'hooks/permissions';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useProjectsQuery } from 'queries';
import { useVendorRolesQuery } from 'queries/roles';
import { useTimesheetApproversMapping } from 'queries/timesheets/approver_mapping';
import { fullName } from 'utils/full-name';
// Importing from index file was causing some breaking changes, hence imported directly from the hook file
// eslint-disable-next-line import/no-internal-modules

export const useTimesheetFilter = (
  userTimesheets: UserTimesheet[],
  approvalType: TimesheetApprovalType,
) => {
  const translate = useTranslate();
  const [searchValue, setSearchValue] = useState('');
  const { data: approversMapping } = useTimesheetApproversMapping();
  const currentUser = useUser();
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;

  const { data } = useProjectsQuery();

  const listOfProjectsIOwn = useMemo(() => {
    const projectIOwn = data
      ?.filter(
        (project) => project.projectOwner.userId === currentUser.user.userId,
      )
      .map((project) => project.projectId);

    return projectIOwn || [];
  }, [currentUser.user.userId, data]);

  const listOfUsersMappedUnderYou = useMemo(() => {
    return Object.keys(approversMapping ?? {}).reduce((acc, curUserId) => {
      if (approversMapping?.[curUserId] === currentUser.user.userId) {
        acc.push(Number(curUserId));
      }

      return acc;
    }, [] as number[]);
  }, [approversMapping, currentUser.user.userId]);

  const isMangerApproval = useMemo(
    () => approvalType === TimesheetApprovalType.Manager,
    [approvalType],
  );

  const viewOptions = useMemo(
    () =>
      FilterViewOptions.reduce(
        (acc, option) => {
          if (option === FilterViewType.MyTeam && !isMangerApproval) {
            return acc;
          }
          if (
            option === FilterViewType.MyTeam &&
            listOfUsersMappedUnderYou.length === 0
          ) {
            return acc;
          }
          if (option === FilterViewType.MyTeam && !isAccountAdmin) {
            return acc;
          }
          if (option === FilterViewType.ProjectsIOwn && isMangerApproval) {
            return acc;
          }
          if (
            option === FilterViewType.ProjectsIOwn &&
            listOfProjectsIOwn.length === 0
          ) {
            return acc;
          }
          if (option === FilterViewType.ProjectsIOwn && !isAccountAdmin) {
            return acc;
          }
          acc.push({
            value: option,
            label: translate(
              `timesheets.view_types.${option}` as TranslationKey,
            ),
            count: getCountOfTimesheetsByFilter(
              userTimesheets,
              option,
              listOfUsersMappedUnderYou,
              listOfProjectsIOwn,
            ),
          });

          return acc;
        },
        [] as {
          value: FilterViewType;
          label: string;
          count: number;
        }[],
      ),
    [
      isAccountAdmin,
      isMangerApproval,
      listOfProjectsIOwn,
      listOfUsersMappedUnderYou,
      translate,
      userTimesheets,
    ],
  );

  const { data: rawRoles = [] } = useVendorRolesQuery({
    staleTime: GENERAL_API_STALE,
  });
  const roles = useMemo(
    () =>
      orderBy(
        rawRoles.map(({ roleName, roleId }) => ({
          label: roleName,
          value: roleId,
        })),
        [({ label }) => label.toLowerCase()],
        ['asc'],
      ),
    [rawRoles],
  );

  const roleOptions = useMemo(() => {
    return [
      {
        value: -1,
        label: translate('timesheets.show_types.all' as TranslationKey),
        count: userTimesheets.length,
      },
    ].concat(
      roles.map((role) => ({
        ...role,
        count: getCountOfTimesheetsByRole(userTimesheets, role.value),
      })),
    );
  }, [roles, userTimesheets, translate]);

  const [selectedViewType, setSelectedViewType] = useState<FilterItem>(
    viewOptions[0],
  );
  const [selectedRole, setSelectedRole] = useState<FilterItem>(roleOptions[0]);

  const filteredTimesheets = useMemo(() => {
    const filterByView = getTimesheetsByFilter(
      userTimesheets,
      selectedViewType.value as FilterViewType,
      listOfUsersMappedUnderYou,
      listOfProjectsIOwn,
    );

    const result =
      selectedRole.value === -1
        ? filterByView
        : getTimesheetsByRole(filterByView, selectedRole.value as number);

    if (searchValue !== '') {
      return result?.filter((member) => {
        if (isMangerApproval) {
          return !searchValue.trim()
            ? true
            : fullName(member.user)
                ?.toLowerCase()
                ?.includes(searchValue?.toLowerCase());
        }

        return !searchValue.trim()
          ? true
          : fullName(member.user)
              ?.toLowerCase()
              ?.includes(searchValue?.toLowerCase()) ||
              member.project.name
                ?.toLowerCase()
                ?.includes(searchValue?.toLowerCase());
      });
    }

    return result;
  }, [
    userTimesheets,
    selectedViewType.value,
    listOfUsersMappedUnderYou,
    listOfProjectsIOwn,
    selectedRole.value,
    searchValue,
    isMangerApproval,
  ]);

  return {
    roleOptions,
    viewOptions,
    selectedViewType,
    setSelectedViewType,
    selectedRole,
    setSelectedRole,
    filteredTimesheets,
    searchValue,
    setSearchValue,
  };
};
